import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, TemplateRef, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { merge, of as observableOf, Subject, EMPTY } from "rxjs"; 
import { catchError, map, startWith, switchMap, takeUntil, delay, filter } from "rxjs/operators";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { RssFeedI } from '../../../interfaces/core.interface';
import { CoreService } from '../../../services/core.service';
import { DialogComponent } from '../../dialog/dialog.component';
import { SocialAiRssFeedsFormComponent } from '../../forms/create-account/rss-feeds-form/rss-feeds-form.component'; 

@Component({
  selector: 'app-social-ai-admin-rss-feed-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginator,
    MatIcon,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatTabsModule,
    SocialAiRssFeedsFormComponent,
    DialogComponent
  ],
  templateUrl: './social-ai-admin-rss-feed-table.component.html',
  styleUrl: './social-ai-admin-rss-feed-table.component.scss'
})
export class SocialAiAdminRssFeedTableComponent implements OnDestroy, AfterViewInit {

  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  rssFeed: RssFeedI = {} as RssFeedI; 

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.companyIdStream$.next();
    }
  }

  get companyID(): string | null {
    return this._companyID;
  }

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild('rssFeedFormTmpl', { static: true }) rssFeedFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;

  dataSource = new MatTableDataSource<RssFeedI>(); 
  displayedColumns: string[] = [
    'ID',
    'feed', 
    'url',  
    'switchboard_template_name',
    'site_name', 
    'isApproved', 
    'actions'
  ];

  totalItems: number = 0;

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) { }

  ngAfterViewInit(): void {
    const sortChangeObservable: import("rxjs").Observable<any> = this.sort ? this.sort.sortChange : EMPTY;
    const pageObservable: import("rxjs").Observable<any> = this.paginator ? this.paginator.page : EMPTY;

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    } else {
      console.warn("MatPaginator is not available in ngAfterViewInit. Check your HTML template.");
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.sort.sortChange.subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
      });
    } else {
      console.warn("MatSort is not available in ngAfterViewInit. Check your HTML template.");
    }

    merge(
      sortChangeObservable,
      pageObservable,
      this.companyIdStream$.asObservable()
    )
      .pipe(
        takeUntil(this.destroy$),
        delay(0),
        filter(() => {
          return this._companyID !== null;
        }),
        startWith({}),
        switchMap(() => this.getFeedsDataObservable()), 
        catchError((error) => {
          console.error("Error in data loading merge pipe:", error);
          return observableOf({ data: [], total: 0 });
        })
      ).subscribe((data) => {
        this.dataSource.data = data.data;
        this.totalItems = data.total;
      });

    if (this._companyID !== null) {
      this.companyIdStream$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFeedsDataObservable() { 
    const params: any = {
      sortBy: this.sort?.active ?? "",
      sortOrder: this.sort?.direction ?? "",
      offset: (this.paginator?.pageIndex ?? 0) * (this.paginator?.pageSize ?? 0),
      limit: this.paginator?.pageSize ?? 0,
    };

    if (this.companyID) {
      params.companyID = Number(this.companyID);
    } else {
      console.warn("getFeedsDataObservable called without companyID in params. This should not happen.");
      return observableOf({ data: [], total: 0 });
    }

    return this.coreService
      .getData("/getFeedsPerCompany", params)
      .pipe(
        catchError((error) => {
          console.error("Error fetching Categories:", error);
          return observableOf({ data: [], total: 0 });
        }),
        map((response) => {
          if (!response || !response.data) {
            return { data: [], total: 0 };
          }

          const uniqueFeeds: RssFeedI[] = [];
          const seenFeeds = new Set<string>();

          response.data.forEach((feed: RssFeedI) => { 
            if (this.companyID && feed.companyID !== Number(this.companyID)) {
              return;
            }

            if (!seenFeeds.has(feed.feed)) { 
              seenFeeds.add(feed.feed);
              uniqueFeeds.push(feed);
            }
          });
          return { data: uniqueFeeds, total: uniqueFeeds.length };
        })
      );
  }

   addNewRssFeed() {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Company ID is not set. Cannot add Category.");
      return;
    }

    this.rssFeed = {
      companyID: Number(this.companyID) 
    } as RssFeedI;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.rssFeedFormTmpl,
        header: 'Add New Category',
        cls: '!bg-slate-600 !text-white',
      },
      width: '500px',
      maxWidth: '100vw'
    });
  }

    editRssFeed(row: RssFeedI) {
    this.rssFeed = { ...row }; 
    
    this.dialog.open(DialogComponent, {
      data: {
        content: this.rssFeedFormTmpl,
        header: 'Edit Category',
        cls: '!bg-slate-600 !text-white'
      },
      width: '500px',
      maxWidth: '100vw'
    });
  }

  openDeleteRssFeedConfirm(row: RssFeedI) { 
    this.rssFeed = row; 
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete Category?',
        cls: 'bg-red-500 !text-white'
      }
    });
  }

    deleteFeed(): void {
    this.coreService
      .deleteData("deleteRssFeed", String(this.rssFeed.id))
      .pipe()
      .subscribe(() => {
        localStorage.removeItem("sites");
        this.onSubmitClbk();
      });
  }

  onStatusChange(element: RssFeedI): void { 
    if (element.isApproved === 'Approved') {
      this.setApprovestatus(element);
    } else if (element.isApproved === 'Not Approved') {
      this.setNotApprovedStatus(element);
    }
  }

  setApprovestatus(row: RssFeedI): void { 
    this.coreService.postData('approveSocialAiRssFeed/approve', { ID: row.id }).subscribe({
      next: (data) => {
        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
        this.coreService.showSnackbar("Error updating approval status.");
      }
    });
  }

  setNotApprovedStatus(row: RssFeedI): void { 
    this.coreService.postData('approveSocialAiRssFeed/decline', { ID: row.id }).subscribe({
      next: (data) => {
        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
        this.coreService.showSnackbar("Error updating approval status.");
      }
    });
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();
    this.companyIdStream$.next();
  }
}